import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getExpenses, getExpensesSummary, getExpensesBalance, createExpense, updateExpense, deleteExpense, seedHistoricalExpenses, exportExpenses, getCostosFijos } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import {
  DollarSign, Plus, Pencil, Trash2, Download, Upload, ChevronDown, ChevronRight,
  TrendingDown, TrendingUp, BarChart3, Loader2, Search, X, Filter, ArrowDownUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'gastos_obligatorios', label: 'Gastos Obligatorios' },
  { value: 'creditos_y_tarjeta', label: 'Créditos y Tarjeta' },
  { value: 'remuneraciones', label: 'Remuneraciones' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'limpieza_y_aseo', label: 'Limpieza y Aseo' },
  { value: 'costos_imprevistos', label: 'Costos Imprevistos' },
  { value: 'oficina_y_amenities', label: 'Oficina y Amenities' },
  { value: 'tiendita', label: 'Tiendita' },
  { value: 'eventos', label: 'Eventos' },
  { value: 'devoluciones', label: 'Devoluciones' },
];

const PAYMENT_METHODS = [
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'mercado_pago', label: 'Mercado Pago' },
];

const CATEGORY_COLORS = {
  gastos_obligatorios: '#C0392B',
  creditos_y_tarjeta: '#8E44AD',
  remuneraciones: '#2B4257',
  marketing: '#E67E22',
  limpieza_y_aseo: '#27AE60',
  costos_imprevistos: '#E74C3C',
  oficina_y_amenities: '#3498DB',
  tiendita: '#F39C12',
  eventos: '#1ABC9C',
  devoluciones: '#95A5A6',
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const formatMonth = (monthStr) => {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
};

// ===== Expense Form Modal =====
const ExpenseForm = ({ expense, onSave, onClose }) => {
  const [form, setForm] = useState({
    category: expense?.category || 'gastos_obligatorios',
    description: expense?.description || '',
    amount: expense?.amount || '',
    payment_date: expense?.payment_date || '',
    month: expense?.month || getCurrentMonth(),
    payment_method: expense?.payment_method || 'transferencia',
    status: expense?.status || 'ok',
    observations: expense?.observations || '',
    tipo_documento: expense?.tipo_documento || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) {
      toast.error('Descripcion y monto son obligatorios');
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...form, amount: parseInt(form.amount) });
      toast.success(expense ? 'Gasto actualizado' : 'Gasto creado');
      onClose();
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="expense-form-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[#E8E2D7]">
          <h3 className="text-lg font-semibold text-[#1A2835]">{expense ? 'Editar Gasto' : 'Nuevo Gasto'}</h3>
          <button onClick={onClose} className="text-[#5C7083] hover:text-[#1A2835]"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5C7083] mb-1">Categoria</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="input-field w-full" data-testid="expense-category-select">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5C7083] mb-1">Mes</label>
              <input type="month" value={form.month} onChange={e => setForm({ ...form, month: e.target.value })}
                className="input-field w-full" data-testid="expense-month-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C7083] mb-1">Descripcion</label>
            <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="input-field w-full" placeholder="Ej: Arriendo, Camila Badilla..." data-testid="expense-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5C7083] mb-1">Monto (CLP)</label>
              <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                className="input-field w-full" placeholder="0" data-testid="expense-amount-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5C7083] mb-1">Fecha de Pago</label>
              <input type="date" value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })}
                className="input-field w-full" data-testid="expense-date-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5C7083] mb-1">Metodo de Pago</label>
              <select value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}
                className="input-field w-full" data-testid="expense-method-select">
                {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5C7083] mb-1">Estado</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                className="input-field w-full" data-testid="expense-status-select">
                <option value="ok">OK</option>
                <option value="pendiente">Pendiente</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5C7083] mb-1">Tipo Documento</label>
              <select value={form.tipo_documento} onChange={e => setForm({ ...form, tipo_documento: e.target.value })}
                className="input-field w-full" data-testid="expense-tipo-doc-select">
                <option value="">Sin especificar</option>
                <option value="boleta">Boleta</option>
                <option value="factura">Factura</option>
              </select>
            </div>
            <div />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C7083] mb-1">Observaciones</label>
            <textarea value={form.observations} onChange={e => setForm({ ...form, observations: e.target.value })}
              className="input-field w-full" rows={2} placeholder="Notas adicionales..." data-testid="expense-observations-input" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-[#2B4257] hover:bg-[#1A2835]" data-testid="expense-save-btn">
              {saving ? <Loader2 className="animate-spin" size={16} /> : expense ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Main Flujo Page =====
export default function Flujo() {
  const { isDirection } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [balance, setBalance] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [expandedCats, setExpandedCats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [costosFijos, setCostosFijos] = useState({ monto: 0, detalle: '' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [expRes, sumRes, balRes, cfRes] = await Promise.all([
        getExpenses(selectedMonth),
        getExpensesSummary(selectedMonth),
        getExpensesBalance(selectedMonth),
        getCostosFijos()
      ]);
      setExpenses(expRes.data);
      setSummary(sumRes.data);
      setBalance(balRes.data);
      setCostosFijos(cfRes.data || { monto: 0, detalle: '' });
      // Auto-expand all categories
      const cats = {};
      (expRes.data || []).forEach(e => { cats[e.category] = true; });
      setExpandedCats(cats);
    } catch (err) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (data) => {
    if (editExpense) {
      await updateExpense(editExpense.id, data);
    } else {
      await createExpense(data);
    }
    setEditExpense(null);
    loadData();
  };

  const handleDelete = async (id, desc) => {
    if (!window.confirm(`Eliminar "${desc}"?`)) return;
    try {
      await deleteExpense(id);
      toast.success('Gasto eliminado');
      loadData();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleExport = async () => {
    try {
      const res = await exportExpenses(selectedMonth);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Gastos_GRIT_${selectedMonth}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch { toast.error('Error al exportar'); }
  };

  const handleSeedHistorical = async () => {
    if (!window.confirm('Importar datos historicos de Enero, Febrero y Marzo 2026?')) return;
    setSeeding(true);
    try {
      const res = await seedHistoricalExpenses();
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar');
    } finally { setSeeding(false); }
  };

  // Group expenses by category
  const grouped = {};
  const filtered = expenses.filter(e => {
    const matchSearch = !searchTerm || e.description?.toLowerCase().includes(searchTerm.toLowerCase()) || e.observations?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = !filterCategory || e.category === filterCategory;
    return matchSearch && matchCat;
  });
  filtered.forEach(e => {
    const cat = e.category || 'otros';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(e);
  });

  if (!isDirection()) {
    return <div className="p-8 text-center text-[#5C7083]">Acceso restringido a Direccion</div>;
  }

  return (
    <div className="space-y-6" data-testid="flujo-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2835]" data-testid="flujo-title">Flujo de Caja</h1>
          <p className="text-sm text-[#5C7083]">
            Control de gastos y egresos
            {expenses.length > 0 && (
              <span className="ml-2 text-xs" data-testid="flujo-last-update">
                | Ultimo movimiento: {expenses.reduce((latest, e) => {
                  const d = e.updated_at || e.payment_date || e.created_at || '';
                  return d > latest ? d : latest;
                }, '').slice(0, 10) || '-'}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
            className="input-field" data-testid="flujo-month-selector" />
          {expenses.length === 0 && !loading && (
            <Button onClick={handleSeedHistorical} disabled={seeding} variant="outline" size="sm" data-testid="btn-seed-historical">
              {seeding ? <Loader2 className="animate-spin mr-1" size={14} /> : <Upload size={14} className="mr-1" />}
              Importar Historico
            </Button>
          )}
          <Button onClick={handleExport} variant="outline" size="sm" data-testid="btn-export-excel">
            <Download size={14} className="mr-1" /> Exportar Excel
          </Button>
          <Button onClick={() => { setEditExpense(null); setShowForm(true); }} className="bg-[#2B4257] hover:bg-[#1A2835]" size="sm" data-testid="btn-add-expense">
            <Plus size={14} className="mr-1" /> Nuevo Gasto
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="flujo-summary">
          <div className="card p-5 bg-gradient-to-br from-[#C0392B] to-[#E74C3C] text-white" data-testid="flujo-total-gastos">
            <p className="text-sm opacity-80 mb-1">Total Gastos - {formatMonth(selectedMonth)}</p>
            <p className="text-3xl font-bold">{formatCurrency(summary.total)}</p>
            <p className="text-sm opacity-70 mt-1">{summary.count} registros</p>
          </div>
          <div className="card p-5" data-testid="flujo-status-ok">
            <p className="text-xs text-[#5C7083] mb-1">Pagados (OK)</p>
            <p className="text-2xl font-bold text-[#27AE60]">{formatCurrency(summary.by_status?.ok || 0)}</p>
          </div>
          <div className="card p-5" data-testid="flujo-status-pendiente">
            <p className="text-xs text-[#5C7083] mb-1">Pendientes</p>
            <p className="text-2xl font-bold text-[#E67E22]">{formatCurrency(summary.by_status?.pendiente || 0)}</p>
          </div>
          <div className="card p-5" data-testid="flujo-top-category">
            <p className="text-xs text-[#5C7083] mb-1">Mayor Gasto</p>
            {summary.by_category?.[0] ? (
              <>
                <p className="text-lg font-bold text-[#2B4257]">{summary.by_category[0].label}</p>
                <p className="text-sm text-[#5C7083]">{formatCurrency(summary.by_category[0].amount)}</p>
              </>
            ) : <p className="text-lg text-[#5C7083]">-</p>}
          </div>
        </div>
      )}

      {/* Balance Mensual: Ingresos vs Gastos */}
      {balance && (
        <div className="card p-5" data-testid="flujo-balance">
          <div className="flex items-center gap-2 mb-4">
            <ArrowDownUp size={16} className="text-[#2B4257]" />
            <h3 className="text-base font-semibold text-[#1A2835]">Balance Mensual - {balance.month_label}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Ingresos */}
            <div className="md:col-span-3 space-y-3">
              <p className="text-xs font-semibold text-[#5C7083] uppercase tracking-wide">Ingresos</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#F9F7F3] rounded-lg p-3">
                  <p className="text-xs text-[#5C7083]">Virtual POS (Neto)</p>
                  <p className="text-lg font-bold text-[#2B4257]">
                    {balance.income.vpos_available ? formatCurrency(balance.income.vpos) : '-'}
                  </p>
                  {balance.income.vpos_available && balance.income.vpos_comision > 0 && (
                    <p className="text-[10px] text-[#5C7083]">Bruto {formatCurrency(balance.income.vpos_bruto)} - {formatCurrency(balance.income.vpos_comision)} comision</p>
                  )}
                  {!balance.income.vpos_available && (
                    <p className="text-[10px] text-[#B0C2D1]">Solo mes actual</p>
                  )}
                </div>
                <div className="bg-[#F9F7F3] rounded-lg p-3">
                  <p className="text-xs text-[#5C7083]">Shopify</p>
                  <p className="text-lg font-bold text-[#2B4257]">{formatCurrency(balance.income.shopify)}</p>
                </div>
                <div className="bg-[#F9F7F3] rounded-lg p-3">
                  <p className="text-xs text-[#5C7083]">Presencial</p>
                  <p className="text-lg font-bold text-[#2B4257]">{formatCurrency(balance.income.presencial)}</p>
                </div>
              </div>
              <div className="flex items-center justify-between bg-[#EBF5EE] rounded-lg p-3">
                <span className="text-sm font-medium text-[#4A7C59]">Total Ingresos</span>
                <span className="text-xl font-bold text-[#4A7C59]">{formatCurrency(balance.income.total)}</span>
              </div>
            </div>
            {/* Balance */}
            <div className="md:col-span-2 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-[#EBF5EE] rounded-lg">
                  <span className="text-sm text-[#4A7C59]">Ingresos</span>
                  <span className="font-bold text-[#4A7C59]">{formatCurrency(balance.income.total)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#FDEDEC] rounded-lg">
                  <span className="text-sm text-[#C0392B]">Gastos</span>
                  <span className="font-bold text-[#C0392B]">-{formatCurrency(balance.expenses)}</span>
                </div>
              </div>
              <div className={`mt-3 p-4 rounded-lg text-center ${balance.balance >= 0 ? 'bg-[#2B4257]' : 'bg-[#C0392B]'}`} data-testid="flujo-balance-result">
                <p className="text-xs text-white/70 mb-1">Resultado del Mes</p>
                <div className="flex items-center justify-center gap-2">
                  {balance.balance >= 0 ? <TrendingUp size={20} className="text-[#4ADE80]" /> : <TrendingDown size={20} className="text-[#FCA5A5]" />}
                  <p className="text-2xl font-bold text-white">{formatCurrency(balance.balance)}</p>
                </div>
                {balance.income.total > 0 && (
                  <p className="text-xs text-white/60 mt-1">Margen: {balance.margin_pct}%</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Costos Fijos vs Flujo */}
      {costosFijos.monto > 0 && balance && (
        <div className="card p-5" data-testid="flujo-costos-fijos">
          <h3 className="text-base font-semibold text-[#1A2835] mb-3">Flujo vs Costos Fijos</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-[#EBF5EE] rounded-lg text-center">
              <p className="text-xs text-[#5C7083]">Ingresos</p>
              <p className="text-lg font-bold text-[#4A7C59]">{formatCurrency(balance.income.total)}</p>
            </div>
            <div className="p-3 bg-[#FFF3E0] rounded-lg text-center">
              <p className="text-xs text-[#5C7083]">Costos Fijos</p>
              <p className="text-lg font-bold text-[#E65100]">{formatCurrency(costosFijos.monto)}</p>
              {costosFijos.detalle && <p className="text-[10px] text-[#5C7083] mt-1">{costosFijos.detalle}</p>}
            </div>
            <div className={`p-3 rounded-lg text-center ${balance.income.total - costosFijos.monto >= 0 ? 'bg-[#EBF5EE]' : 'bg-[#FDEDEC]'}`}>
              <p className="text-xs text-[#5C7083]">Diferencia</p>
              <p className={`text-lg font-bold ${balance.income.total - costosFijos.monto >= 0 ? 'text-[#4A7C59]' : 'text-[#C0392B]'}`}>
                {formatCurrency(balance.income.total - costosFijos.monto)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Category Breakdown Chart */}
      {summary?.by_category?.length > 0 && (
        <div className="card p-5" data-testid="flujo-category-breakdown">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={16} className="text-[#2B4257]" />
            <h3 className="text-base font-semibold text-[#1A2835]">Desglose por Categoria - {formatMonth(selectedMonth)}</h3>
          </div>
          <div className="space-y-3">
            {summary.by_category.map((cat) => {
              const pct = summary.total > 0 ? (cat.amount / summary.total * 100) : 0;
              return (
                <div key={cat.category} className="flex items-center gap-3">
                  <div className="w-36 text-sm text-[#1A2835] font-medium truncate">{cat.label}</div>
                  <div className="flex-1 bg-[#F0EDE6] rounded-full h-6 overflow-hidden">
                    <div className="h-full rounded-full flex items-center px-2 text-xs text-white font-medium transition-all duration-500"
                      style={{ width: `${Math.max(pct, 3)}%`, backgroundColor: CATEGORY_COLORS[cat.category] || '#5C7083' }}>
                      {pct >= 8 ? `${pct.toFixed(1)}%` : ''}
                    </div>
                  </div>
                  <div className="w-28 text-right text-sm font-semibold text-[#1A2835]">{formatCurrency(cat.amount)}</div>
                  <div className="w-12 text-right text-xs text-[#5C7083]">{cat.count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search & Filter Bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C7083]" />
          <input type="text" placeholder="Buscar por descripcion u observacion..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="input-field w-full pl-9" data-testid="flujo-search" />
        </div>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          className="input-field" data-testid="flujo-filter-category">
          <option value="">Todas las categorias</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      {/* Expenses Table Grouped by Category */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-[#2B4257]" size={32} />
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="card p-12 text-center" data-testid="flujo-empty">
          <TrendingDown size={48} className="mx-auto text-[#B0C2D1] mb-4" />
          <p className="text-[#5C7083] text-lg">No hay gastos registrados para {formatMonth(selectedMonth)}</p>
          <p className="text-sm text-[#B0C2D1] mt-1">Usa "Nuevo Gasto" para agregar o "Importar Historico" para cargar datos del Excel</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="flujo-expense-list">
          {CATEGORIES.filter(c => grouped[c.value]).map(cat => {
            const items = grouped[cat.value] || [];
            const catTotal = items.reduce((s, e) => s + (e.amount || 0), 0);
            const isExpanded = expandedCats[cat.value];
            return (
              <div key={cat.value} className="card overflow-hidden" data-testid={`flujo-cat-${cat.value}`}>
                <button onClick={() => setExpandedCats(p => ({ ...p, [cat.value]: !p[cat.value] }))}
                  className="w-full flex items-center justify-between p-4 hover:bg-[#F9F7F3] transition-colors">
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[cat.value] }} />
                    <span className="font-semibold text-[#1A2835]">{cat.label}</span>
                    <span className="text-xs text-[#5C7083] bg-[#F0EDE6] px-2 py-0.5 rounded-full">{items.length}</span>
                  </div>
                  <span className="font-bold text-[#1A2835]">{formatCurrency(catTotal)}</span>
                </button>
                {isExpanded && (
                  <div className="border-t border-[#E8E2D7]">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#F9F7F3] text-[#5C7083] text-xs">
                          <th className="px-4 py-2 text-left">Descripcion</th>
                          <th className="px-4 py-2 text-right">Monto</th>
                          <th className="px-4 py-2 text-left">Fecha</th>
                          <th className="px-4 py-2 text-left">Metodo</th>
                          <th className="px-4 py-2 text-center">Estado</th>
                          <th className="px-4 py-2 text-left">Observaciones</th>
                          <th className="px-4 py-2 text-center w-20">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(e => (
                          <tr key={e.id} className="border-t border-[#F0EDE6] hover:bg-[#FDFCF9]" data-testid={`expense-row-${e.id}`}>
                            <td className="px-4 py-2.5 font-medium text-[#1A2835]">{e.description}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-[#1A2835]">{formatCurrency(e.amount)}</td>
                            <td className="px-4 py-2.5 text-[#5C7083]">{formatDate(e.payment_date)}</td>
                            <td className="px-4 py-2.5 text-[#5C7083] capitalize">{e.payment_method?.replace('_', ' ')}</td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${e.status === 'ok' ? 'bg-[#D5F5E3] text-[#27AE60]' : 'bg-[#FDEBD0] text-[#E67E22]'}`}>
                                {e.status === 'ok' ? 'OK' : 'Pendiente'}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-[#5C7083] text-xs max-w-[200px] truncate">{e.observations}</td>
                            <td className="px-4 py-2.5 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => { setEditExpense(e); setShowForm(true); }}
                                  className="p-1 text-[#5C7083] hover:text-[#2B4257] transition-colors" data-testid={`btn-edit-${e.id}`}>
                                  <Pencil size={14} />
                                </button>
                                <button onClick={() => handleDelete(e.id, e.description)}
                                  className="p-1 text-[#5C7083] hover:text-[#C0392B] transition-colors" data-testid={`btn-delete-${e.id}`}>
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <ExpenseForm
          expense={editExpense}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditExpense(null); }}
        />
      )}
    </div>
  );
}
